<template>
  <v-container>
    <v-card title="Video Downloads">
      <v-card-text>
        <v-data-table-server
          v-model="selected"
          v-model:items-per-page="pagination.limit"
          :items="teams"
          :headers="headersTeam"
          :items-length="count"
          :loading="loading"
          :search="pagination.q"
          item-key="id"
          show-select
          return-object
          @update:options="handleLimitChange">
          <template #item.is_late="{ item }">
            <div>
              {{
                year?.turn_deadline && item.movie && item.movie?.created_on > year?.turn_deadline
                  ? 'Late'
                  : ''
              }}
            </div>
          </template>
          <template #item.movie.download_url="{ item }">
            <div v-if="item.movie && item.movie.download_url">
              <a
                :href="item.movie.download_url"
                download
                class="text-decoration-none text-primary text-body-2 font-weight-medium">
                <v-btn block color="primary" class="rounded-lg mb-8" elevation="2"> Movie </v-btn>
              </a>
            </div>
          </template>
          <template #item.teamVideo.download_url="{ item }">
            <div v-if="item.teamVideo && item.teamVideo.download_url">
              <a
                :href="item.teamVideo.download_url"
                download
                class="text-decoration-none text-primary text-body-2 font-weight-medium">
                <v-btn block color="primary" class="rounded-lg mb-8" elevation="2">
                  Team Video
                </v-btn>
              </a>
            </div>
          </template>
          <template #item.screeningCopy.download_url="{ item }">
            <div v-if="item.screeningCopy && item.screeningCopy.download_url">
              <a
                :href="item.screeningCopy.download_url"
                download
                class="text-decoration-none text-primary text-body-2 font-weight-medium">
                <v-btn block color="primary" class="rounded-lg mb-8" elevation="2">
                  Screening Copy
                </v-btn>
              </a>
            </div>
          </template>
          <template #item.btsVideo.download_url="{ item }">
            <div v-if="item.bts && item.bts.download_url">
              <a
                :href="item.bts.download_url"
                download
                class="text-decoration-none text-primary text-body-2 font-weight-medium">
                <v-btn block color="primary" class="rounded-lg mb-8" elevation="2"> BTS </v-btn>
              </a>
            </div>
          </template>
          <template v-slot:top>
            <v-text-field
              v-model="pagination.q"
              class="ma-2"
              density="compact"
              placeholder="Search team..."
              hide-details></v-text-field>
          </template>
        </v-data-table-server>
      </v-card-text>
      <v-card-actions v-if="selected.length > 0">
        <v-row>
          <v-col cols="12" md="3">
            <FullWidthButton type="button" @click="downloadFiles('Movies')">Movies</FullWidthButton>
          </v-col>
          <v-col cols="12" md="3">
            <FullWidthButton type="button" @click="downloadFiles('Team')"
              >Team Videos</FullWidthButton
            >
          </v-col>
          <v-col cols="12" md="3">
            <FullWidthButton type="button" @click="downloadFiles('BTS')">BTS</FullWidthButton>
          </v-col>
          <v-col cols="12" md="3">
            <FullWidthButton type="button" @click="downloadFiles('Screening')"
              >Screening Copies</FullWidthButton
            >
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { VDataTableServer } from 'vuetify/components';
import { IHeaderDTO, IPaginationDTO, ISubmissionDTO, PaginationDTO } from '@/dtos';
import { SNACKSTATUS } from '@/stores/util';
import { useAdminSubmissionStore } from '@/stores/modules/admin';
import { useYearStore } from '@/stores';

const emit = defineEmits<{
  (e: 'snack', objType: string, snackType: SNACKSTATUS, err?: string): void;
}>();
interface Props {
  branchId: string;
  yearId: string;
}
const props = defineProps<Props>();
const branchId = computed(() => props.branchId);
const yearId = computed(() => props.yearId);
const yearStore = useYearStore();
const { data: year } = yearStore.getYearByIdQuery(yearId);
const submissionStore = useAdminSubmissionStore();
const pagination = ref<IPaginationDTO>(new PaginationDTO());
const query = submissionStore.getAllSubmissions(branchId, yearId, pagination);
const loading = computed(() => query.isLoading.value);
const teams = computed(() => query.data.value?.data ?? []);
const count = computed(() => query.data.value?.count ?? 0);
const selected = ref<ISubmissionDTO[]>([]);
const headersTeam = [
  { title: 'Name', key: 'teamCriteria.team.name', align: 'start', sortable: true },
  { title: 'Late', key: 'is_late', sortable: false },
  {
    title: 'Movie',
    key: 'movie.download_url',
    sortable: false,
  },
  {
    title: 'Team',
    key: 'teamVideo.download_url',
    sortable: false,
  },
  {
    title: 'BTS',
    key: 'btsVideo.download_url',
    sortable: false,
  },
  {
    title: 'Screening',
    key: 'screeningCopy.download_url',
    sortable: false,
  },
  { title: '', key: 'action', sortable: false },
] as IHeaderDTO[];

const handleLimitChange = (value: VDataTableServer) => {
  pagination.value.buildPagination(value);
};

const downloadFiles = async (type: string) => {
  let links: string[] = [];

  if (type === 'Movies') {
    links = selected.value
      .map((item) => item.movie?.download_url)
      .filter((url): url is string => !!url);
  } else if (type === 'Team') {
    links = selected.value
      .map((item) => item.teamVideo?.download_url)
      .filter((url): url is string => !!url);
  } else if (type === 'BTS') {
    links = selected.value
      .map((item) => item.bts?.download_url)
      .filter((url): url is string => !!url);
  } else if (type === 'Screening Copies') {
    links = selected.value
      .map((item) => item.screeningCopy?.download_url)
      .filter((url): url is string => !!url);
  }

  for (const [i, url] of links.entries()) {
    const a = document.createElement('a');
    a.href = url;

    const urlParts = url.split('/');
    const filename = urlParts[urlParts.length - 1] || `file-${i + 1}`;

    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // wait 3 seconds before the next one
    await new Promise((res) => setTimeout(res, 1000));
  }
};
</script>
